import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { FluentTooltip } from "../../../../../../components/FluentUI/Fluent-Tooltip";
import TaskMemberAvatarPill from "../../../../../../components/TaskAssignMember/TaskMemberAvatarPill.jsx";

// SELECTION cap only: at most 50 specific Users/Groups may be added per Member Permission
// (AC: "Max 50 specific Users/Groups per Member Permission per Sync configuration"). This is
// NOT a display cap — the dropdown renders every matching member so they remain discoverable.
export const MAX_SYNC_TARGETS = 50;

const memberKey = (m) => `${m.IsGroup ? "g" : "u"}-${m.Id}`;

/**
 * Department-scoped typeahead + removable chips for one Member Permission's
 * "Add specific Users/Groups" operation. Presentational: receives the available
 * department members + the currently selected list and emits onAdd / onRemove.
 * Reuses TaskMemberAvatarPill so the blue-user / red-group avatars match the
 * Edit Task → Assign Members surface.
 */
const SyncMemberSearch = ({ permissionKey, members, departmentMembers, onAdd, onRemove, disabled }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  // Index of the keyboard-highlighted suggestion (-1 = none). Drives aria-activedescendant.
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);
  const listboxId = `sync-suggestions-${permissionKey}`;
  const optionId = (i) => `${listboxId}-opt-${i}`;

  const selectedKeys = useMemo(() => new Set(members.map(memberKey)), [members]);
  const atMax = members.length >= MAX_SYNC_TARGETS;

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const available = (departmentMembers || [])
      // Inactive users/groups cannot be added (Archived/moved members never reach the
      // roster, so this completes the "no unavailable members can be selected" guarantee).
      .filter((m) => !m.Inactive)
      .filter((m) => !selectedKeys.has(memberKey(m)))
      .filter((m) => !q || (m.Name || "").toLowerCase().includes(q));
    // No render cap — every matching member is shown (the 50 limit is a selection cap, not a
    // display one). Users first, then Groups at the end. (TAD-5797)
    const users = available.filter((m) => !m.IsGroup);
    // TAD-5797 (AC 2.b.ii.1): Groups are NOT selectable on the Owner permission — Tasks with a
    // Group as Owner are unsupported at the platform level until TAD-4934 ships. Owner surfaces
    // Users only. // To be removed once we have groups as owners
    const groups = permissionKey === "owner" ? [] : available.filter((m) => m.IsGroup);
    return [...users, ...groups];
  }, [departmentMembers, selectedKeys, query, permissionKey]);

  const handleAdd = (m) => {
    if (atMax) return;
    onAdd(m);
    setQuery("");
    setOpen(false);
    setActiveIndex(-1);
  };

  // Reset the keyboard highlight whenever the filtered list changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  // Keep the highlighted option scrolled into view during keyboard navigation.
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    listRef.current.children[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  const handleKeyDown = (e) => {
    if (atMax) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      // Only act when an option is highlighted; preventDefault stops the modal form submitting.
      if (open && activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handleAdd(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <FluentProvider theme={webLightTheme} className="sync-member-fluent">
    <div className="sync-member-search">
      {members.length > 0 && (
        <div className="sync-member-chips">
          {members.map((m) => {
            const chip = (
              <div
                className={`sync-member-chip ${m.IsGroup ? "is-a-group" : ""} ${m.Missing ? "is-missing" : ""} ${m.Inactive ? "is-inactive" : ""}`}
                data-testid={`sync-chip-${m.Name}`}
              >
                <TaskMemberAvatarPill user={{ IsGroup: m.IsGroup, Name: m.Name }} permission={permissionKey} size={28} />
                <span className="sync-member-chip-name">{m.Name}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="sync-member-chip-remove"
                    aria-label={`Remove ${m.Name}`}
                    onClick={() => onRemove(m)}
                  >
                    &times;
                  </button>
                )}
              </div>
            );
            // Unavailable members: explain why via the shared Fluent tooltip.
            // Archived (soft-deleted) > moved to another Department > Inactive (deactivated).
            const memberType = m.IsGroup ? "Group" : "User";
            const unavailableReason = m.Missing
              ? m.Archived
                ? `${memberType} is Archived`
                : "User/Group no longer in Department"
              : m.Inactive
                ? `${memberType} is Inactive`
                : null;
            if (unavailableReason) {
              return <FluentTooltip key={memberKey(m)} text={unavailableReason} target={chip} />;
            }

            // Available group: show its members on hover, like the Assign Members component. The
            // roster (departmentMembers) carries each group's members via GetAllGroups.ViewMembers.
            const groupMembers = m.IsGroup
              ? departmentMembers.find((d) => d.IsGroup && String(d.Id) === String(m.Id))?.Members || []
              : [];
            if (groupMembers.length > 0) {
              return (
                <OverlayTrigger
                  key={memberKey(m)}
                  trigger={["hover", "focus"]}
                  placement="bottom"
                  delay={{ show: 150, hide: 150 }}
                  overlay={
                    <Popover id={`sync-group-members-${m.Id}`} className="sync-group-members-popover">
                      <Popover.Title>Group Members</Popover.Title>
                      <Popover.Content>
                        <FluentProvider theme={webLightTheme}>
                          {groupMembers.map((gm) => (
                            <div key={gm.Id} className="sync-group-member-row">
                              <TaskMemberAvatarPill user={{ Name: gm.Name }} permission={permissionKey} size={24} />
                              <span className="sync-group-member-name">{gm.Name}</span>
                            </div>
                          ))}
                        </FluentProvider>
                      </Popover.Content>
                    </Popover>
                  }
                >
                  {chip}
                </OverlayTrigger>
              );
            }

            return <React.Fragment key={memberKey(m)}>{chip}</React.Fragment>;
          })}
        </div>
      )}

      {!disabled && (
        <div className="sync-member-search-input-wrap">
          <input
            type="text"
            className="form-control sync-member-input"
            placeholder={atMax ? `Maximum ${MAX_SYNC_TARGETS} reached` : "Search Users/Groups..."}
            value={query}
            disabled={atMax}
            role="combobox"
            aria-expanded={open && suggestions.length > 0}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              open && activeIndex >= 0 && activeIndex < suggestions.length
                ? optionId(activeIndex)
                : undefined
            }
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              setOpen(false);
              setActiveIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            data-testid={`sync-search-${permissionKey}`}
          />
          {open && suggestions.length > 0 && (
            <ul className="sync-member-suggestions" role="listbox" id={listboxId} ref={listRef}>
              {suggestions.map((m, index) => (
                <li
                  key={memberKey(m)}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`sync-member-suggestion ${index === activeIndex ? "is-active" : ""}`}
                  // onMouseDown fires before the input's onBlur so the click registers.
                  onMouseDown={() => handleAdd(m)}
                  onMouseEnter={() => setActiveIndex(index)}
                  data-testid={`sync-suggestion-${m.Name}`}
                >
                  <TaskMemberAvatarPill user={{ IsGroup: m.IsGroup, Name: m.Name }} permission={permissionKey} size={28} />
                  <span className="sync-member-suggestion-name">{m.Name}</span>
                  <span className="sync-member-suggestion-type">{m.IsGroup ? "Group" : "User"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
    </FluentProvider>
  );
};

SyncMemberSearch.propTypes = {
  permissionKey: PropTypes.oneOf(["owner", "editor", "approver", "viewer"]).isRequired,
  members: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      Name: PropTypes.string,
      IsGroup: PropTypes.bool,
      Missing: PropTypes.bool,
      Inactive: PropTypes.bool,
    })
  ).isRequired,
  departmentMembers: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      Name: PropTypes.string,
      IsGroup: PropTypes.bool,
      Inactive: PropTypes.bool,
      // For groups: the member roster shown on chip hover ([{ Id, Name }]).
      Members: PropTypes.array,
    })
  ),
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SyncMemberSearch.defaultProps = {
  departmentMembers: [],
  disabled: false,
};

export default SyncMemberSearch;
